// The Status filter every master-data list carries, on References and on Rules
// (owner, 2026-09-01, extended to Rules 2026-09-01). It replaced a two-state
// switch — "Show retired" on References, "Show inactive" on Rules — which could
// show active-only or everything and gave no way to ask for the inactive rows
// on their own.
//
// Shared code rather than a copy per screen, because "Active is the default"
// and "an absent flag means active" are decisions that have to hold everywhere
// or the same list answers differently on two tabs.

/** One Status box. */
export type StatusValue = 'active' | 'inactive'

/**
 * THE STATUS FILTER IS THE TICKED BOXES (owner, 2026-10-14: a multi-select of
 * Active and Inactive, "as if 'Any' was never invented"). A row passes when its
 * status is ticked; with nothing ticked the filter does not narrow, as with
 * every multi-select. Ticking both shows every row because both statuses are
 * ticked, not because of a special case.
 */
export type StatusFilter = readonly StatusValue[]

/** Where every list opens, and where Reset returns it. */
export const DEFAULT_STATUS_FILTER: StatusFilter = ['active']

export const STATUS_FILTER_LABEL: Record<StatusValue, string> = {
  // No "Status:" inside the value (owner, 2026-10-09): the field is labelled
  // Status already.
  active: 'Active',
  inactive: 'Inactive',
}

/** Does a row's `is_active` pass the filter?
 *
 *  Takes `boolean | undefined` on purpose: the two half masters (accounts,
 *  departments) leave `is_active` OFF a row that has never been retired, and
 *  an absent flag means active there. Reading it as "not active" would open the
 *  Accounts and Departments views empty. */
export function matchesStatus(filter: StatusFilter, isActive: boolean | undefined): boolean {
  if (filter.length === 0) return true
  return filter.includes(isActive === false ? 'inactive' : 'active')
}

/** How many filters are narrowing a References or Rules list right now — the
    number in the Filter button (owner, 2026-09-11: "the button should indicate
    whether something is active").
 *
 *  STATUS: ACTIVE COUNTS AS ONE, which is the owner's own reading ("by default
 *  there would be one active filter, since we are filtering for active
 *  records") and the honest one: the default view is NOT the whole table, and a
 *  control that looked idle while hiding every retired row is how someone
 *  concludes a record was deleted. It counts while it hides rows: one box
 *  ticked. Neither or both hides nothing.
 *
 *  Lives here rather than beside the button so `ListFilters.tsx` exports
 *  components alone — the rule is logic, and this is where the rest of this
 *  filter's logic already is. */
export function activeFilterCount(filter: StatusFilter | undefined, term: string): number {
  return (filter !== undefined && filter.length === 1 ? 1 : 0) + (term.trim() !== '' ? 1 : 0)
}
